/* ============================================================
   COD ORDER FORM v5 — assets/cod-order-form.js
   New features:
   - Sticky Add-to-Cart bar (mobile only, visible when main
     COD button scrolls out of view via IntersectionObserver)
   - All v4 features preserved
   ============================================================ */
(function () {
  'use strict';

  const $ = (s) => document.querySelector(s);

  const SELECTORS = {
    overlay:         '#cod-form-overlay',
    openBtn:         '#cod-open-btn',
    closeBtn:        '#cod-close-btn',
    form:            '#cod-order-form',
    successScreen:   '#cod-success-screen',
    orderIdDisplay:  '#cod-order-id-display',
    successClose:    '#cod-success-close',
    submitBtn:       '#cod-submit-btn',
    btnText:         '#cod-btn-text',
    prepaidBtn:      '#cod-prepaid-btn',
    prepaidLoader:   '#cod-prepaid-loader',
    prepaidPrice:    '#cod-prepaid-price',
    pinLoader:       '#cod-pin-loader',
    qtyMinus:        '#cod-qty-minus',
    qtyPlus:         '#cod-qty-plus',
    qtyDisplay:      '#cod-qty-display',
    qtyInput:        '#cod-quantity',
    total:           '#cod-total',
    honeypot:        '#cod-honeypot',
    // Confirm popup
    confirmOverlay:  '#cod-confirm-overlay',
    confirmYes:      '#cod-confirm-yes',
    confirmYesText:  '#cod-confirm-yes-text',
    confirmLoader:   '#cod-confirm-loader',
    confirmNo:       '#cod-confirm-no',
    confirmName:     '#cod-confirm-name',
    confirmMobile:   '#cod-confirm-mobile',
    confirmAddress:  '#cod-confirm-address',
    confirmTotal:    '#cod-confirm-total',
    confirmQtyLine:  '#cod-confirm-qty-line',
    confirmCheckbox: '#cod-confirm-checkbox',
    confirmCheckErr: '#cod-confirm-checkbox-error',
    // Cancel
    cancelStrip:     '#cod-cancel-strip',
    cancelBtn:       '#cod-cancel-btn',
    cancelBtnText:   '#cod-cancel-btn-text',
    cancelLoader:    '#cod-cancel-loader',
    cancelCountdown: '#cod-cancel-countdown',
    cancelledMsg:    '#cod-cancelled-msg',
    timerRingFill:   '#cod-timer-ring-fill',
    // Reorder
    reorderBtn:      '#cod-reorder-btn',
    // Sticky bar
    stickyBar:       '#cod-sticky-bar',
    stickyOpenBtn:   '#cod-sticky-open-btn',
    triggerArea:     '#cod-trigger-area',
    stickyVariant:   '#cod-sticky-variant',
    stickyPrice:     '#cod-sticky-price',
    // Variant label in form product strip
    variantLabel:    '#cod-variant-label-text',
    stripPrice:      '#cod-strip-price',
    // Variant picker
    vpickOverlay:    '#cod-vpick-overlay',
    vpickClose:      '#cod-vpick-close',
    vpickOptions:    '#cod-vpick-options',
    vpickOrderBtn:   '#cod-vpick-order-btn',
    vpickBtnPrice:   '#cod-vpick-btn-price',
  };

  const PIN_API         = 'https://api.postalpincode.in/pincode/';
  const MAX_SESS_ORDERS = 3;

  let isOpen         = false;
  let isSubmitting   = false;
  let qty            = 1;
  let sessionOrders  = 0;
  let pinTimer       = null;
  let utmParams      = {};
  let cancelTimer    = null;
  let currentOrderId = null;
  let cancelExpired  = false;
  let pendingPayload = null;

  const config    = window.__COD_CONFIG__ || {};
  const PROXY     = (config.proxyUrl || '').replace(/\/$/, '');
  const CANCEL_WINDOW_SECONDS = (config.cancelWindowMin || 10) * 60;

  // ── Variant state ────────────────────────────────────────
  // Tracks the currently selected variant across page interactions.
  // Starts from the Liquid-rendered default; updates when the
  // shopper picks a different size/colour on the product page.
  let currentVariant = {
    id:        config.variantId    || 0,
    price:     config.variantPrice || 0,
    title:     '',          // filled by setVariantById() on init
    compareAt: 0,
    available: true,
  };
  // True once the customer has explicitly interacted with a variant
  // selector on the page (not just the default on load).
  let userSelectedVariant = false;

  // Convenience: price per unit for current variant
  const getUnitPrice = () => currentVariant.price;

  /* ─────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    captureUTM();
    setupListeners();
    prefillFromURL();
    syncVariantFromPage();          // ← detect variant changes from product page
    setVariantById(config.variantId, false); // ← init title from variants array
    updatePrices();
    initPixels();
    initStickyBar();
    fireAllPixels('ViewContent', {
      content_ids:  [String(currentVariant.id)],
      content_name: config.productTitle,
      content_type: 'product',
      value:        currentVariant.price,
      currency:     'INR',
    });
  });

  /* ─────────────────────────────────────────────────────────
     UTM CAPTURE
  ───────────────────────────────────────────────────────── */
  function captureUTM() {
    const p = new URLSearchParams(window.location.search);
    ['utm_source','utm_medium','utm_campaign','utm_content','utm_term','fbclid','ttclid','gclid'].forEach(k => {
      const v = p.get(k) || sessionStorage.getItem(k) || '';
      if (v) sessionStorage.setItem(k, v);
      utmParams[k] = v;
      const el = $(`#cod-${k.replace(/_/g,'-')}`);
      if (el) el.value = v;
    });
    const u = $('#cod-page-url'); if (u) u.value = window.location.href;
    const r = $('#cod-referrer'); if (r) r.value = document.referrer || '';
  }

  /* ─────────────────────────────────────────────────────────
     EVENT LISTENERS
  ───────────────────────────────────────────────────────── */
  function setupListeners() {
    const overlay        = $(SELECTORS.overlay);
    const confirmOverlay = $(SELECTORS.confirmOverlay);

   $(SELECTORS.openBtn)?.addEventListener('click', handleOpenBtnClick);
    $(SELECTORS.closeBtn)?.addEventListener('click', closeForm);
    $(SELECTORS.successClose)?.addEventListener('click', closeAll);
    $(SELECTORS.reorderBtn)?.addEventListener('click', handleReorder);
    // Sticky bar: guard variant selection for multi-variant products
    $(SELECTORS.stickyOpenBtn)?.addEventListener('click', handleOpenBtnClick);

    // Variant picker — close handlers
    $(SELECTORS.vpickClose)?.addEventListener('click', hideVariantPicker);
    $(SELECTORS.vpickOverlay)?.addEventListener('click', e => {
      if (e.target === $(SELECTORS.vpickOverlay)) hideVariantPicker();
    });
    // Variant card buttons — tap to select only (picker stays open)
    $(SELECTORS.vpickOptions)?.querySelectorAll('.cod-vpick-option').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.dataset.variantAvailable === 'false') return;
        const id = parseInt(btn.dataset.variantId, 10);
        setVariantById(id, true);
        haptic('light');
      });
    });
    // ORDER NOW footer button inside picker → close picker then open form
    $(SELECTORS.vpickOrderBtn)?.addEventListener('click', () => {
      hideVariantPicker();
      setTimeout(() => openForm(), 120);
    });

    overlay?.addEventListener('click', e => { if (e.target === overlay) closeForm(); });
    confirmOverlay?.addEventListener('click', e => { if (e.target === confirmOverlay) hideConfirm(); });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (confirmOverlay?.classList.contains('is-open')) hideConfirm();
        else if (isOpen) closeForm();
      }
    });

    $(SELECTORS.form)?.addEventListener('submit', handleFormSubmit);
    $(SELECTORS.confirmYes)?.addEventListener('click', handleConfirmYes);
    $(SELECTORS.confirmNo)?.addEventListener('click', handleConfirmNo);
    $(SELECTORS.prepaidBtn)?.addEventListener('click', handlePrepaidClick);
    $(SELECTORS.cancelBtn)?.addEventListener('click', handleCancelOrder);
    $(SELECTORS.qtyMinus)?.addEventListener('click', () => updateQty(-1));
    $(SELECTORS.qtyPlus)?.addEventListener('click',  () => updateQty(1));

    // Reset checkbox error on change
    $(SELECTORS.confirmCheckbox)?.addEventListener('change', () => {
      const errEl = $(SELECTORS.confirmCheckErr);
      if (errEl) errEl.textContent = '';
    });

    ['cod-mobile','cod-alt-mobile'].forEach(id => {
      $(`#${id}`)?.addEventListener('input', function() {
        this.value = this.value.replace(/\D/g,'').slice(0,10);
      });
    });

    $('#cod-full-name')?.addEventListener('input', function() {
      this.value = this.value.replace(/[^a-zA-Z\s.'-]/g,'');
    });

    $('#cod-pincode')?.addEventListener('input', function() {
      this.value = this.value.replace(/\D/g,'');
      if (this.value.length === 6) {
        clearTimeout(pinTimer);
        pinTimer = setTimeout(() => lookupPin(this.value), 350);
      } else clearPinResult();
    });

    document.querySelectorAll('.cod-input[data-validate]').forEach(el => {
      el.addEventListener('blur', () => validateField(el));
    });
  }

  /* ─────────────────────────────────────────────────────────
     OPEN / CLOSE
  ───────────────────────────────────────────────────────── */
  function openForm() {
    const ov = $(SELECTORS.overlay);
    if (!ov) return;
    isOpen = true;
    ov.classList.add('is-open');
    ov.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    $(SELECTORS.openBtn)?.setAttribute('aria-expanded','true');
    $(SELECTORS.stickyBar)?.classList.add('is-hidden-by-modal');
    setTimeout(() => ov.querySelector('input:not(.cod-honeypot)')?.focus(), 380);
    fireAllPixels('InitiateCheckout', {
      content_ids:[String(currentVariant.id)], value:currentVariant.price*qty, currency:'INR', num_items:qty,
    });
    haptic('light');
  }

  function closeForm() {
    const ov = $(SELECTORS.overlay);
    if (!ov) return;
    isOpen = false;
    ov.classList.remove('is-open');
    ov.setAttribute('aria-hidden','true');
    document.body.style.overflow = '';
    $(SELECTORS.stickyBar)?.classList.remove('is-hidden-by-modal');
    $(SELECTORS.openBtn)?.setAttribute('aria-expanded','false');
    $(SELECTORS.openBtn)?.focus();
  }

  function closeAll() {
    closeForm();
    hideConfirm();
    hideVariantPicker();
    stopCancelTimer();
  }

  /* ─────────────────────────────────────────────────────────
     QTY & PRICES
  ───────────────────────────────────────────────────────── */
  function updateQty(d) {
    qty = Math.max(1, Math.min(10, qty + d));
    const disp = $(SELECTORS.qtyDisplay);
    const inp  = $(SELECTORS.qtyInput);
    if (disp) disp.textContent = qty;
    if (inp)  inp.value = qty;
    updatePrices();
    haptic('light');
  }

  function updatePrices() {
    const total = getUnitPrice() * qty;
    const totEl = $(SELECTORS.total);
    if (totEl) totEl.textContent = '₹' + fmtINR(total);
    const discount = config.prepaidDiscount || 10;
    const ppEl = $(SELECTORS.prepaidPrice);
    if (ppEl) ppEl.textContent = fmtINR(total * (1 - discount / 100));
    // Keep product strip price in sync
    const stripPriceEl = $(SELECTORS.stripPrice);
    if (stripPriceEl) stripPriceEl.textContent = '₹' + fmtINR(getUnitPrice());
  }

  function fmtINR(n) { return Math.round(n).toLocaleString('en-IN'); }

  /* ─────────────────────────────────────────────────────────
     PIN LOOKUP
  ───────────────────────────────────────────────────────── */
  async function lookupPin(pin) {
    const loader = $(SELECTORS.pinLoader);
    clearPinResult();
    if (loader) loader.classList.add('is-loading');
    try {
      const r = await fetch(`${PIN_API}${pin}`);
      const d = await r.json();
      if (!d?.[0] || d[0].Status !== 'Success') { showErr('cod-pincode','Invalid PIN code.'); return; }
      const state = d[0].PostOffice[0].State;
      const sel = $('#cod-state');
      if (sel && state) {
        const opt = Array.from(sel.options).find(o => o.value.toLowerCase() === state.toLowerCase());
        if (opt) {
          sel.value = opt.value;
          sel.classList.add('is-valid'); sel.classList.remove('is-error');
          clearErr('cod-state');
          const suc = $('#cod-pincode-success');
          if (suc) suc.textContent = '✓ ' + opt.value;
        }
      }
      const pinEl = $('#cod-pincode');
      if (pinEl) { pinEl.classList.add('is-valid'); pinEl.classList.remove('is-error'); }
    } catch(e) { /* silent */ }
    finally { if (loader) loader.classList.remove('is-loading'); }
  }

  function clearPinResult() {
    const s = $('#cod-pincode-success'); if (s) s.textContent = '';
    clearErr('cod-pincode');
  }
  

  /* ─────────────────────────────────────────────────────────
     VALIDATION
  ───────────────────────────────────────────────────────── */
  const FAKE_NAMES = ['test','fake','demo','asdf','sample','admin','user','name','john doe','jane doe','unknown','xxxxx'];
  const FAKE_ADDR  = [/^(a+|b+|x+|test|fake|na|n\/a|null|none|xxx)$/i, /^(.)\1{4,}$/, /^(asd|qwe|zxc|abc|xyz|123|111)/i];

  function validateField(el) {
    const v = el.value.trim(), t = el.dataset.validate;
    if (t === 'name')       return valName(v, el);
    if (t === 'mobile')     return valMobile(v, el, 'cod-mobile');
    if (t === 'alt-mobile') return valAltMobile(v, el);
    if (t === 'pincode')    return valPin(v, el);
    if (t === 'state')      return valReq(v, el, 'cod-state', 'Select your state.');
    if (t === 'city')       return valCity(v, el);
    if (t === 'address1')   return valAddr(v, el, 'cod-address1', 'House/Flat No.');
    if (t === 'address2')   return valAddr(v, el, 'cod-address2', 'Street/Area/Colony');
    return true;
  }

  function valName(v, el) {
    if (!v)              return inv(el,'cod-full-name','Full name required.');
    if (v.length < 3)    return inv(el,'cod-full-name','Minimum 3 characters.');
    if (!/^[a-zA-Z\s.'-]{3,60}$/.test(v)) return inv(el,'cod-full-name','Only letters allowed.');
    if (/^(.)\1+$/.test(v.replace(/\s/g,''))) return inv(el,'cod-full-name','Enter your real name.');
    if (FAKE_NAMES.some(n => v.toLowerCase().includes(n))) return inv(el,'cod-full-name','Enter your actual name.');
    return ok(el,'cod-full-name');
  }
  function valMobile(v, el, id) {
    if (!v)                      return inv(el,id,'Mobile number required.');
    if (!/^[6-9]\d{9}$/.test(v)) return inv(el,id,'Valid 10-digit number required (starts 6–9).');
    if (/^(\d)\1{9}$/.test(v))   return inv(el,id,'Enter a real mobile number.');
    return ok(el,id);
  }
  function valAltMobile(v, el) {
    if (!v) return ok(el,'cod-alt-mobile');
    if (!/^[6-9]\d{9}$/.test(v)) return inv(el,'cod-alt-mobile','Invalid alternate number.');
    if (v === $('#cod-mobile')?.value.trim()) return inv(el,'cod-alt-mobile','Must differ from primary.');
    return ok(el,'cod-alt-mobile');
  }
  function valPin(v, el) {
    if (!v)                       return inv(el,'cod-pincode','PIN code required.');
    if (!/^[1-9]\d{5}$/.test(v)) return inv(el,'cod-pincode','Valid 6-digit PIN required.');
    return ok(el,'cod-pincode');
  }
  function valReq(v, el, id, msg) { return v ? ok(el,id) : inv(el,id,msg); }
  function valCity(v, el) {
    if (!v || v.length < 2) return inv(el,'cod-city','Enter your city.');
    if (!/^[a-zA-Z\s.'-]{2,50}$/.test(v)) return inv(el,'cod-city','City name looks invalid.');
    return ok(el,'cod-city');
  }
  function valAddr(v, el, id, label) {
    if (!v || v.length < 4) return inv(el,id,`${label} is required.`);
    if (FAKE_ADDR.some(p => p.test(v.toLowerCase()))) return inv(el,id,'Please enter real address.');
    return ok(el,id);
  }
  function ok(el,id)     { el.classList.add('is-valid'); el.classList.remove('is-error'); clearErr(id); return true; }
  function inv(el,id,m)  { el.classList.add('is-error'); el.classList.remove('is-valid'); showErr(id,m); return false; }
  function showErr(id,m) { const e=$(`#${id}-error`); if(e) e.textContent=m; }
  function clearErr(id)  { const e=$(`#${id}-error`); if(e) e.textContent=''; }

  function validateAll() {
    return Array.from(document.querySelectorAll('.cod-input[data-validate]'))
                .map(el => validateField(el)).every(Boolean);
  }

  /* ─────────────────────────────────────────────────────────
     ANTI-FRAUD
  ───────────────────────────────────────────────────────── */
  function isFraud() {
    if (sessionOrders >= MAX_SESS_ORDERS) return 'Too many orders this session. Please wait.';
    if ($(SELECTORS.honeypot)?.value)     return 'BOT';
    const mob  = $('#cod-mobile')?.value.trim();
    const used = JSON.parse(sessionStorage.getItem('cod_mobs') || '[]');
    if (used.includes(mob)) return 'An order was already placed with this mobile number.';
    return null;
  }

  /* ─────────────────────────────────────────────────────────
     PAYLOAD BUILDER
  ───────────────────────────────────────────────────────── */
  function buildPayload() {
    const full  = $('#cod-full-name').value.trim();
    const parts = full.split(/\s+/);
    return {
      variantId:  currentVariant.id,
      quantity:   qty,
      unitPrice:  currentVariant.price,
      totalPrice: currentVariant.price * qty,
      variantTitle: currentVariant.title,
      firstName:  parts[0],
      lastName:   parts.slice(1).join(' ') || '.',
      fullName:   full,
      mobile:     $('#cod-mobile').value.trim(),
      altMobile:  ($('#cod-alt-mobile')?.value || '').trim(),
      address1:   $('#cod-address1').value.trim(),
      address2:   $('#cod-address2').value.trim(),
      city:       $('#cod-city').value.trim(),
      state:      $('#cod-state').value.trim(),
      pincode:    $('#cod-pincode').value.trim(),
      landmark:   ($('#cod-landmark')?.value || '').trim(),
      pageUrl:    window.location.href,
      referrer:   document.referrer || '',
      ...utmParams,
    };
  }

  /* ─────────────────────────────────────────────────────────
     STEP 1: FORM SUBMIT → Show Confirm Popup
  ───────────────────────────────────────────────────────── */
  function handleFormSubmit(e) {
    e.preventDefault();
    if (isSubmitting) return;

    if (!validateAll()) {
      document.querySelector('.cod-input.is-error')?.scrollIntoView({behavior:'smooth',block:'center'});
      haptic('error');
      return;
    }

    const fraud = isFraud();
    if (fraud) { if (fraud !== 'BOT') alert(fraud); return; }

    // Reset checkbox before showing popup
    const cb = $(SELECTORS.confirmCheckbox);
    if (cb) cb.checked = false;
    const cbErr = $(SELECTORS.confirmCheckErr);
    if (cbErr) cbErr.textContent = '';

    pendingPayload = buildPayload();
    showConfirmPopup(pendingPayload);
  }

  /* ─────────────────────────────────────────────────────────
     CONFIRM POPUP
  ───────────────────────────────────────────────────────── */
  function showConfirmPopup(p) {
    const nameEl = $(SELECTORS.confirmName);
    const mobEl  = $(SELECTORS.confirmMobile);
    const adrEl  = $(SELECTORS.confirmAddress);
    const totEl  = $(SELECTORS.confirmTotal);
    const qtyEl  = $(SELECTORS.confirmQtyLine);

    if (nameEl) nameEl.textContent = p.fullName;
    if (mobEl)  mobEl.textContent  = '+91 ' + p.mobile;
    if (adrEl)  adrEl.textContent  = [p.address1, p.address2, p.landmark, p.city, p.state, p.pincode].filter(Boolean).join(', ');
    if (totEl)  totEl.textContent  = '₹' + fmtINR(p.totalPrice);
    if (qtyEl)  qtyEl.textContent  = `Qty: ${p.quantity}`;

    const ov = $(SELECTORS.confirmOverlay);
    if (ov) {
      ov.classList.add('is-open');
      ov.removeAttribute('aria-hidden');
      setTimeout(() => $(SELECTORS.confirmCheckbox)?.focus(), 100);
    }
    haptic('light');
  }

  function hideConfirm() {
    const ov = $(SELECTORS.confirmOverlay);
    if (ov) { ov.classList.remove('is-open'); ov.setAttribute('aria-hidden','true'); }
  }

  /* ─────────────────────────────────────────────────────────
     STEP 2: Confirm Yes — validate checkbox first
  ───────────────────────────────────────────────────────── */
  async function handleConfirmYes() {
    if (isSubmitting || !pendingPayload) return;

    // Validate checkbox
    const cb    = $(SELECTORS.confirmCheckbox);
    const cbErr = $(SELECTORS.confirmCheckErr);
    if (cb && !cb.checked) {
      if (cbErr) cbErr.textContent = 'Please confirm you will accept the delivery.';
      cb.focus();
      haptic('error');
      return;
    }

    isSubmitting = true;

    const yesBtn = $(SELECTORS.confirmYes);
    const loader = $(SELECTORS.confirmLoader);
    const yesText= $(SELECTORS.confirmYesText);
    if (yesBtn)  yesBtn.disabled    = true;
    if (yesText) yesText.textContent = 'Placing…';
    if (loader)  loader.classList.add('is-loading');

    try {
      fireAllPixels('AddPaymentInfo', {
        content_ids:[String(config.variantId)],
        value: pendingPayload.totalPrice, currency:'INR', num_items: qty,
      });

      const result = await createCODOrder(pendingPayload);
      if (!result.success) throw new Error(result.error || 'Order failed');

      sessionOrders++;
      const used = JSON.parse(sessionStorage.getItem('cod_mobs') || '[]');
      used.push(pendingPayload.mobile);
      sessionStorage.setItem('cod_mobs', JSON.stringify(used));

      currentOrderId = result.orderId;

      fireAllPixels('Purchase', {
        content_ids:[String(config.variantId)], content_name:config.productTitle,
        content_type:'product', value:pendingPayload.totalPrice, currency:'INR',
        num_items:qty, order_id:result.orderId,
      });
      sendAllCAPI('Purchase', pendingPayload, result.orderId, pendingPayload.totalPrice);

      hideConfirm();
      showSuccess(result.orderId, result.orderName);
      haptic('success');

    } catch(err) {
      console.error('[COD confirm]', err);
      hideConfirm();
      alert('Something went wrong. Please try again.');
      haptic('error');
    } finally {
      if (yesBtn)  yesBtn.disabled    = false;
      if (yesText) yesText.textContent = 'Yes, Place Order';
      if (loader)  loader.classList.remove('is-loading');
      isSubmitting = false;
    }
  }

  function handleConfirmNo() {
    hideConfirm();
    pendingPayload = null;
    setTimeout(() => $('#cod-full-name')?.focus(), 200);
  }

  /* ─────────────────────────────────────────────────────────
     SUCCESS SCREEN
  ───────────────────────────────────────────────────────── */
  function showSuccess(orderId, orderName) {
    const form  = $(SELECTORS.form);
    const succ  = $(SELECTORS.successScreen);
    const strip = $('.cod-product-strip');
    const hdr   = $('.cod-modal__header');

    if (form)  form.style.display  = 'none';
    if (strip) strip.style.display = 'none';
    if (hdr)   hdr.style.display   = 'none';
    if (succ)  succ.style.display  = 'block';

    const disp = $(SELECTORS.orderIdDisplay);
    if (disp && orderName) disp.textContent = orderName;

    const amtEl = document.querySelector('#cod-success-amount');
  if (amtEl) amtEl.textContent = fmtINR(pendingPayload?.totalPrice || unitPrice * qty);

    cancelExpired = false;

    const cancelStrip = $(SELECTORS.cancelStrip);
    const cancelBtn   = $(SELECTORS.cancelBtn);
    if (cancelStrip) cancelStrip.style.display = 'flex';
    if (cancelBtn)   cancelBtn.style.display   = 'flex';

    

    startCancelCountdown();
  }

  /* ─────────────────────────────────────────────────────────
     CANCEL COUNTDOWN
  ───────────────────────────────────────────────────────── */
  function startCancelCountdown() {
    let remaining = CANCEL_WINDOW_SECONDS;
    const total   = CANCEL_WINDOW_SECONDS;
    updateCountdownUI(remaining, total);
    cancelTimer = setInterval(() => {
      remaining--;
      if (remaining <= 0) { stopCancelTimer(); expireCancelWindow(); return; }
      updateCountdownUI(remaining, total);
    }, 1000);
  }

  function updateCountdownUI(remaining, total) {
    const el = $(SELECTORS.cancelCountdown);
    if (el) {
      const m = Math.floor(remaining / 60);
      const s = remaining % 60;
      el.textContent = `${m}:${String(s).padStart(2,'0')}`;
    }
    const ring = $(SELECTORS.timerRingFill);
    if (ring) {
      const pct = (remaining / total) * 100;
      ring.style.strokeDasharray = `${pct} 100`;
      ring.style.stroke = pct > 50 ? '#16a34a' : pct > 20 ? '#f59e0b' : '#dc2626';
    }
  }

  function stopCancelTimer() {
    if (cancelTimer) { clearInterval(cancelTimer); cancelTimer = null; }
  }

  function expireCancelWindow() {
    cancelExpired = true;
    const strip = $(SELECTORS.cancelStrip);
    const btn   = $(SELECTORS.cancelBtn);
    if (strip) strip.innerHTML = '<span class="cod-cancel-expired">⏰ Cancel window closed</span>';
    if (btn)   btn.style.display = 'none';
  }

  /* ─────────────────────────────────────────────────────────
     CANCEL ORDER
  ───────────────────────────────────────────────────────── */
  async function handleCancelOrder() {
    if (cancelExpired || !currentOrderId) return;

    const confirmed = window.confirm('Are you sure you want to cancel this order?');
    if (!confirmed) return;

    const btn    = $(SELECTORS.cancelBtn);
    const loader = $(SELECTORS.cancelLoader);
    const text   = $(SELECTORS.cancelBtnText);

    if (btn)    btn.disabled     = true;
    if (text)   text.textContent = 'Cancelling…';
    if (loader) loader.classList.add('is-loading');

    try {
      const result = await cancelOrder(currentOrderId);
      if (!result.success) throw new Error(result.error);

      stopCancelTimer();

      const strip = $(SELECTORS.cancelStrip);
      if (strip) strip.style.display = 'none';
      if (btn)   btn.style.display   = 'none';

      const msg = $(SELECTORS.cancelledMsg);
      if (msg) msg.style.display = 'flex';

      haptic('success');
      currentOrderId = null;

    } catch(err) {
      console.error('[Cancel]', err);
      alert('Could not cancel the order. Please call us to cancel.');
      if (btn)    btn.disabled     = false;
      if (text)   text.textContent = 'Cancel This Order';
      if (loader) loader.classList.remove('is-loading');
    }
  }

  /* ─────────────────────────────────────────────────────────
     REORDER
  ───────────────────────────────────────────────────────── */
  function handleReorder() {
    const succ         = $(SELECTORS.successScreen);
    const form         = $(SELECTORS.form);
    const strip        = $('.cod-product-strip');
    const hdr          = $('.cod-modal__header');
    const cancelledMsg = $(SELECTORS.cancelledMsg);
    const cancelStrip  = $(SELECTORS.cancelStrip);
    const cancelBtn    = $(SELECTORS.cancelBtn);

    if (succ)         succ.style.display         = 'none';
    if (form)         form.style.display          = '';
    if (strip)        strip.style.display         = '';
    if (hdr)          hdr.style.display           = '';
    if (cancelledMsg) cancelledMsg.style.display  = 'none';
    if (cancelStrip)  cancelStrip.style.display   = 'none';
    if (cancelBtn)    cancelBtn.style.display      = 'none';

    stopCancelTimer();
    cancelExpired  = false;
    currentOrderId = null;
    pendingPayload = null;
    sessionOrders  = Math.max(0, sessionOrders - 1);

    $('#cod-full-name')?.focus();
    haptic('light');
  }

  /* ─────────────────────────────────────────────────────────
     PREPAID FLOW
  ───────────────────────────────────────────────────────── */
  async function handlePrepaidClick() {
    if (isSubmitting) return;
    if (!validateAll()) {
      document.querySelector('.cod-input.is-error')?.scrollIntoView({behavior:'smooth',block:'center'});
      haptic('error'); return;
    }

    const payload = buildPayload();
    const btn     = $(SELECTORS.prepaidBtn);
    const loader  = $(SELECTORS.prepaidLoader);
    if (btn)    btn.disabled = true;
    if (loader) loader.classList.add('is-loading');
    isSubmitting = true;

    try {
      const result = await createDraftOrderWithDiscount(payload);
      if (!result.success) throw new Error(result.error);
      window.location.href = result.checkoutUrl;
    } catch(err) {
      console.error('[Prepaid]', err);
      alert('Could not initiate payment. Please use COD or try again.');
      haptic('error');
      if (btn)    btn.disabled = false;
      if (loader) loader.classList.remove('is-loading');
    } finally {
      isSubmitting = false;
    }
  }

  /* ─────────────────────────────────────────────────────────
     API CALLS
  ───────────────────────────────────────────────────────── */
  async function createCODOrder(p) {
    const order = {
      order: {
        line_items: [{ variant_id: p.variantId, quantity: p.quantity, price: p.unitPrice.toFixed(2) }],
        customer:   { first_name: p.firstName, last_name: p.lastName, phone: '+91' + p.mobile },
        billing_address:  buildAddr(p),
        shipping_address: buildAddr(p),
        financial_status: 'pending',
        send_receipt: false,
        send_fulfillment_receipt: false,
        note:             buildNote(p),
        note_attributes:  buildNoteAttrs(p),
        tags:             buildTags(p),
        payment_gateway_names: ['Cash on Delivery'],
        transactions: [{ kind:'authorization', status:'success', amount: p.totalPrice.toFixed(2), gateway:'Cash on Delivery' }],
      }
    };
    const r = await fetch(`${PROXY}/apps/cod-proxy/create-order`, {
      method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(order),
    });
    if (!r.ok) { const d=await r.json().catch(()=>({})); return {success:false, error:d.message||`HTTP ${r.status}`}; }
    const d = await r.json();
    return { success:true, orderId:d.order?.id, orderName:d.order?.name };
  }

  async function cancelOrder(orderId) {
    const r = await fetch(`${PROXY}/apps/cod-proxy/cancel-order`, {
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ orderId, reason:'customer' }),
    });
    if (!r.ok) { const d=await r.json().catch(()=>({})); return {success:false, error:d.message||`HTTP ${r.status}`}; }
    const d = await r.json();
    return { success: d.success || true };
  }

  async function createDraftOrderWithDiscount(p) {
    const discount = config.prepaidDiscount || 10;
    const body = {
      draft_order: {
        line_items: [{ variant_id: p.variantId, quantity: p.quantity }],
        customer:   { first_name: p.firstName, last_name: p.lastName, phone: '+91' + p.mobile },
        shipping_address: buildAddr(p),
        billing_address:  buildAddr(p),
        applied_discount: {
          description: `Prepaid Discount ${discount}%`,
          value_type:  'percentage',
          value:       String(discount),
          title:       `PREPAID${discount}`,
          amount:      (p.totalPrice * discount / 100).toFixed(2),
        },
        note:            buildNote(p),
        note_attributes: buildNoteAttrs(p, 'PREPAID'),
        tags:            buildTags(p, 'PREPAID'),
        use_customer_default_address: false,
      }
    };
    const r = await fetch(`${PROXY}/apps/cod-proxy/create-draft-order`, {
      method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body),
    });
    if (!r.ok) { const d=await r.json().catch(()=>({})); return {success:false, error:d.message||`HTTP ${r.status}`}; }
    const d = await r.json();
    return { success:true, checkoutUrl: d.checkout_url };
  }

  /* ─────────────────────────────────────────────────────────
     ORDER BUILDERS
  ───────────────────────────────────────────────────────── */
  function buildAddr(p) {
    return {
      first_name: p.firstName, last_name: p.lastName,
      phone: '+91' + p.mobile,
      address1: p.address1,
      address2: [p.address2, p.landmark].filter(Boolean).join(', '),
      city: p.city, province: p.state,
      country: 'India', country_code: 'IN', zip: p.pincode,
    };
  }

  function buildNote(p) {
    return [
      `Mobile: +91${p.mobile}`,
      p.altMobile ? `Alt: +91${p.altMobile}` : null,
      p.variantTitle ? `Variant: ${p.variantTitle}` : null,
      `${p.address1}, ${p.address2}${p.landmark ? ', Near '+p.landmark : ''}`,
      `${p.city}, ${p.state} - ${p.pincode}`,
    ].filter(Boolean).join('\n');
  }

  function buildNoteAttrs(p, orderType = 'COD') {
    const a = [
      { name:'Order Type', value:orderType },
      { name:'Mobile',     value:'+91'+p.mobile },
      { name:'Full Name',  value:p.fullName },
    ];
    if (p.variantTitle) a.push({ name:'Variant',        value:p.variantTitle });
    if (p.altMobile)    a.push({ name:'Alt Mobile',      value:'+91'+p.altMobile });
    if (p.utm_source)   a.push({ name:'UTM Source',      value:p.utm_source });
    if (p.utm_medium)   a.push({ name:'UTM Medium',      value:p.utm_medium });
    if (p.utm_campaign) a.push({ name:'UTM Campaign',    value:p.utm_campaign });
    if (p.utm_content)  a.push({ name:'UTM Content',     value:p.utm_content });
    if (p.utm_term)     a.push({ name:'UTM Term',        value:p.utm_term });
    if (p.fbclid)       a.push({ name:'FB Click ID',     value:p.fbclid });
    if (p.ttclid)       a.push({ name:'TikTok Click ID', value:p.ttclid });
    if (p.gclid)        a.push({ name:'Google Click ID', value:p.gclid });
    if (p.pageUrl)      a.push({ name:'Landing Page',    value:p.pageUrl });
    if (p.referrer)     a.push({ name:'Referrer',        value:p.referrer });
    return a;
  }

  function buildTags(p, type = 'COD') {
    const t = [type, 'Website Order'];
    if (p.utm_source)   t.push(`src:${p.utm_source}`);
    if (p.utm_campaign) t.push(`cmp:${p.utm_campaign}`);
    if (p.fbclid)  t.push('Facebook Ad');
    if (p.ttclid)  t.push('TikTok Ad');
    if (p.gclid)   t.push('Google Ad');
    return t.join(', ');
  }

  /* ─────────────────────────────────────────────────────────
     MULTI-PIXEL
  ───────────────────────────────────────────────────────── */
  function initPixels() {
    const pixels = config.fbPixels || [];
    if (!pixels.length) return;
    if (!window.fbq) {
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
      (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    }
    pixels.forEach(px => { if (px.id) fbq('init', px.id); });
    fbq('track', 'PageView');
  }

  function fireAllPixels(event, params) {
    if (typeof fbq === 'undefined') return;
    const pixels = config.fbPixels || [];
    if (!pixels.length) { fbq('track', event, params); return; }
    pixels.forEach(px => { if (px.id) fbq('trackSingle', px.id, event, params); });
  }

  async function sendAllCAPI(eventName, payload, orderId, value) {
    const pixels = config.fbPixels || [];
    if (!pixels.length) return;
    const evTime = Math.floor(Date.now() / 1000);
    const evId   = `${eventName}_${orderId}_${evTime}`;
    const ud = {
      ph:      [await sha256('+91'+payload.mobile)],
      fn:      [await sha256(payload.firstName.toLowerCase())],
      ln:      [await sha256(payload.lastName.toLowerCase())],
      ct:      [await sha256(payload.city.toLowerCase().replace(/\s/g,''))],
      st:      [await sha256(payload.state.toLowerCase().replace(/\s/g,''))],
      zp:      [await sha256(payload.pincode)],
      country: [await sha256('in')],
      client_user_agent: navigator.userAgent,
    };
    const fbp = getCookie('_fbp'); if (fbp) ud.fbp = fbp;
    if (payload.fbclid) ud.fbc = `fb.1.${Date.now()}.${payload.fbclid}`;

    const ev = {
      event_name:eventName, event_time:evTime, event_id:evId,
      action_source:'website', event_source_url:payload.pageUrl,
      user_data:ud,
      custom_data:{ value:value.toFixed(2), currency:'INR', content_ids:[String(config.variantId)], content_type:'product', order_id:String(orderId), num_items:payload.quantity },
    };

    await Promise.allSettled(pixels.map(px => {
      if (!px.id) return;
      return fetch(`${PROXY}/apps/cod-proxy/conversions-api`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ pixelId:px.id, events:[ev] }),
      }).catch(()=>{});
    }));
  }

  /* ─────────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────────── */
  async function sha256(str) {
    if (!str) return '';
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join('');
  }
  function getCookie(n) { const m=document.cookie.match(new RegExp('(^| )'+n+'=([^;]+)')); return m?m[2]:null; }
  function haptic(t) {
    if (!navigator.vibrate) return;
    navigator.vibrate({light:[15],success:[20,80,20],error:[50,30,50]}[t]||[15]);
  }
  function prefillFromURL() {
    const p = new URLSearchParams(window.location.search);
    const n = p.get('customer_name'), ph = p.get('customer_phone');
    if (n)  { const e=$('#cod-full-name'); if(e) e.value=decodeURIComponent(n); }
    if (ph) { const e=$('#cod-mobile');    if(e) e.value=ph.replace(/^\+91/,'').replace(/\D/g,'').slice(0,10); }
  }

  /* ─────────────────────────────────────────────────────────
     VARIANT MANAGEMENT
     setVariantById  — update currentVariant state + all UI
     syncVariantFromPage — listen to the theme's own selectors
     openFormOrPickVariant — sticky bar entry point
  ───────────────────────────────────────────────────────── */

  /**
   * Set the active variant by ID.
   * @param {number} id            - Shopify variant ID
   * @param {boolean} byUser       - true when customer explicitly picked it
   */
  function setVariantById(id, byUser = false) {
    const variants = config.variants || [];
    const found = variants.find(v => v.id === id || v.id === Number(id));
    if (!found) return;

    currentVariant = {
      id:        found.id,
      price:     found.price,
      title:     found.title,
      compareAt: found.compareAt || 0,
      available: found.available !== false,
    };
    if (byUser) userSelectedVariant = true;

    // Update hidden variant input in form
    const vi = $('#cod-variant-id');
    if (vi) vi.value = currentVariant.id;

    // Update variant label inside product strip
    const labelEl = $(SELECTORS.variantLabel);
    if (labelEl) labelEl.textContent = currentVariant.title;

    // Update sticky bar variant label + price
    const stickyVariantEl = $(SELECTORS.stickyVariant);
    if (stickyVariantEl) stickyVariantEl.textContent = currentVariant.title;
    const stickyPriceEl = $(SELECTORS.stickyPrice);
    if (stickyPriceEl) stickyPriceEl.textContent = '₹' + fmtINR(currentVariant.price);

    // Recalculate totals
    updatePrices();

    // Highlight matching option button in picker (if open)
    $(SELECTORS.vpickOptions)?.querySelectorAll('.cod-vpick-option').forEach(btn => {
      const active = parseInt(btn.dataset.variantId, 10) === currentVariant.id;
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-checked', String(active));
    });

    // Keep ORDER NOW button price in picker in sync
    const vpickBtnPriceEl = $(SELECTORS.vpickBtnPrice);
    if (vpickBtnPriceEl) vpickBtnPriceEl.textContent = fmtINR(currentVariant.price);
  }

  /**
   * Listen for variant changes from the product page's own selectors.
   * Covers: native <select name="id">, radio inputs, URL param changes,
   * and the custom `variant:changed` event dispatched by Dawn + most themes.
   */
  function syncVariantFromPage() {
    // 1. URL param on load (e.g. /products/tshirt?variant=12345)
    const urlId = new URLSearchParams(location.search).get('variant');
    if (urlId) setVariantById(parseInt(urlId, 10), false);

    // 2. Native <select name="id"> — used by many Shopify themes
    const nativeSel = document.querySelector('select[name="id"]');
    if (nativeSel) {
      nativeSel.addEventListener('change', () => {
        setVariantById(parseInt(nativeSel.value, 10), true);
      });
    }

    // 3. Radio inputs <input type="radio" name="id"> — used by some themes
    document.querySelectorAll('input[type="radio"][name="id"]').forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) setVariantById(parseInt(radio.value, 10), true);
      });
    });

    // 4. Custom event — Dawn, Prestige, Turbo, and many premium themes
    //    dispatch 'variant:changed' on document with e.detail.variant
    document.addEventListener('variant:changed', (e) => {
      const vid = e?.detail?.variant?.id;
      if (vid) setVariantById(vid, true);
    });

    // 5. Some AJAX themes update the URL via history.pushState without a full reload
    const origPush = history.pushState.bind(history);
    history.pushState = function (...args) {
      origPush(...args);
      const vid = new URLSearchParams(location.search).get('variant');
      if (vid) setVariantById(parseInt(vid, 10), true);
    };
    const origReplace = history.replaceState.bind(history);
    history.replaceState = function (...args) {
      origReplace(...args);
      const vid = new URLSearchParams(location.search).get('variant');
      if (vid) setVariantById(parseInt(vid, 10), true);
    };

    // 6. Catch-all: MutationObserver on any element that holds the variant ID
    //    (some headless / custom themes write it into a data attribute)
    const variantHolders = document.querySelectorAll('[data-product-id], [data-variant-id]');
    if (variantHolders.length) {
      const mo = new MutationObserver(() => {
        variantHolders.forEach(el => {
          const vid = el.dataset.variantId;
          if (vid && parseInt(vid, 10) !== currentVariant.id) {
            setVariantById(parseInt(vid, 10), true);
          }
        });
      });
      variantHolders.forEach(el => mo.observe(el, { attributes: true, attributeFilter: ['data-variant-id'] }));
    }
  }

  /**
   * Called when the STICKY BAR button is tapped.
   * - Single-variant products: open form immediately.
   * - Multi-variant products + user already selected: open form.
   * - Multi-variant products + no explicit selection: show variant picker first.
   */
  function openFormOrPickVariant() {
    if (!config.hasMultipleVariants) {
      openForm();
      return;
    }
    if (userSelectedVariant) {
      openForm();
      return;
    }
    // Customer hasn't explicitly chosen — show picker
    showVariantPicker();
  }

  function showVariantPicker() {
    const ov = $(SELECTORS.vpickOverlay);
    if (!ov) { openForm(); return; } // fallback if liquid didn't render picker
    ov.classList.add('is-open');
    ov.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    // Mark current variant in picker
    setVariantById(currentVariant.id, false);
    haptic('light');
  }

  function hideVariantPicker() {
    const ov = $(SELECTORS.vpickOverlay);
    if (!ov) return;
    ov.classList.remove('is-open');
    ov.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

 

  /* ──────────────────────────────────────────────────────────
   NEW FUNCTIONS TO ADD
   
   Find the initStickyBar() function near the bottom.
   PASTE ALL OF THIS CODE right BEFORE initStickyBar():
────────────────────────────────────────────────────────── */
 
  /* ─────────────────────────────────────────────────────────
     SMART OPEN — checks variants first
  ───────────────────────────────────────────────────────── */
  function handleOpenBtnClick() {
    // If product has multiple variants → show variant picker first
    if (config.hasMultipleVariants) {
      openVariantPicker();
    } else {
      // Single variant → open form directly
      openForm();
    }
  }
 
  /* ─────────────────────────────────────────────────────────
     VARIANT PICKER
  ───────────────────────────────────────────────────────── */
  function openVariantPicker() {
    const ov = $(SELECTORS.vpickOverlay);
    if (!ov) {
      // Fallback — no picker in DOM, just open form directly
      openForm();
      return;
    }
    ov.classList.add('is-open');
    ov.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    haptic('light');

    // Sync current variant selection into picker UI
    setVariantById(currentVariant.id, false);

    // Close button (once only to avoid stacking)
    $(SELECTORS.vpickClose)?.addEventListener('click', closeVariantPicker, { once: true });

    // Close on backdrop tap
    ov.addEventListener('click', function onBgClick(e) {
      if (e.target === ov) {
        closeVariantPicker();
        ov.removeEventListener('click', onBgClick);
      }
    });

    // Variant card buttons — clone to remove stale listeners, then re-bind
    // Tapping a card SELECTS it but does NOT open the form
    document.querySelectorAll('.cod-vpick-option:not([disabled])').forEach(btn => {
      const fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', function() {
        const id = parseInt(this.dataset.variantId, 10);
        setVariantById(id, true);
        haptic('light');
      });
    });

    // ORDER NOW footer button — close picker then open form
    const orderBtnEl = $('#cod-vpick-order-btn');
    if (orderBtnEl) {
      const freshOrderBtn = orderBtnEl.cloneNode(true);
      orderBtnEl.parentNode.replaceChild(freshOrderBtn, orderBtnEl);
      freshOrderBtn.addEventListener('click', () => {
        closeVariantPicker();
        setTimeout(() => openForm(), 120);
      });
    }
  }
 
  function closeVariantPicker() {
    const ov = $(SELECTORS.vpickOverlay);
    if (!ov) return;
    ov.classList.remove('is-open');
    ov.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
 
  function selectVariant(variantId, variantPrice, variantTitle, available) {
    if (!available) return;
    // Picker stays open — just update selected variant state.
    // The ORDER NOW footer button in the picker handles close + form open.
    setVariantById(variantId, true);
    haptic('light');
  }
 
  /* ─────────────────────────────────────────────────────────
     END OF NEW FUNCTIONS — initStickyBar() follows after this
  ───────────────────────────────────────────────────────── */
 /* ─────────────────────────────────────────────────────────
     STICKY BAR — show on mobile when trigger scrolls away
  ───────────────────────────────────────────────────────── */

  function initStickyBar() {
    const bar        = $(SELECTORS.stickyBar);
    const triggerEl  = $(SELECTORS.triggerArea);
    if (!bar || !triggerEl) return;

    // Only activate on mobile (≤519px). On desktop the bar is
    // display:none via CSS, but skip the Observer entirely for clarity.
    const mq = window.matchMedia('(max-width: 519px)');
    if (!mq.matches) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Main button is visible — hide sticky bar
            bar.classList.remove('is-visible');
            bar.setAttribute('aria-hidden', 'true');
          } else {
            // Main button is off-screen — show sticky bar
            // (but not if the modal is currently open)
            if (!isOpen) {
              bar.classList.add('is-visible');
              bar.removeAttribute('aria-hidden');
            }
          }
        });
      },
      {
        threshold: 0,
        // Treat button as "off-screen" when it's fully gone from viewport
        rootMargin: '0px',
      }
    );

    observer.observe(triggerEl);

    // Re-evaluate if viewport resizes across breakpoint
    mq.addEventListener('change', (e) => {
      if (!e.matches) {
        observer.disconnect();
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
      }
    });
  }

})();